import { compareRetailerPreference } from '../domain/retailerPreference';
// ShuttleSelection.js
import React, { useState, useEffect } from 'react';
import '../FishFinderBuilder.css';
import { shuttlesData } from '../data/shuttlesData';
import ImageModal from './ImageModal';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import { fetchProductPrices, fetchProductNamesWithOffers, resolveProductNameForSupabase } from '../utils/api';

const GridContainer = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 20px;
  width: 100%;
  max-width: 100%;
  margin: 0 auto;
  padding: 20px 0;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

const ImageContainer = styled.div`
  width: 100%;
  height: 80px;
  background-color: #ffffff;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  position: relative;

  @media (min-width: 769px) {
    height: 100px;
  }
`;

const ProductImage = styled.img`
  width: 85%;
  height: 85%;
  object-fit: contain;

  @media (min-width: 769px) {
    width: 95%;
    height: 95%;
  }
`;

const ShuttleSelection = ({
  shuttle = null,
  setShuttle = () => {},
  prevStep = () => {},
  nextStep = () => {},
  resetAll = () => {},
  jumpToStep = () => {},
  isInModal = false,
  onNavigateToFullPage = () => {}
}) => {
  const navigate = useNavigate();
  const [failedImages, setFailedImages] = useState({});
  const [modalImage, setModalImage] = useState({
    isOpen: false,
    url: '',
    alt: ''
  });
  const [, setSelectedShuttleId] = useState(shuttle?.id || null);
  const [expandedCards, setExpandedCards] = useState(new Set());
  const [assemblyPreferences, setAssemblyPreferences] = useState(() => {
    // Load saved preferences from localStorage
    try {
      const saved = localStorage.getItem('shuttleAssemblyPreferences');
      return saved ? JSON.parse(saved) : {};
    } catch (error) {
      console.error('Error loading assembly preferences:', error);
      return {};
    }
  }); // Store assembly preference per shuttle family
  const [shuttlePricing, setShuttlePricing] = useState({}); // Store pricing data for shuttles

  // Add filter state
  const [filters, setFilters] = useState({
    assemblyType: {
      assembled: false,
      diy: false
    },
    poleHolders: false,
    uvLight: false,
    mountingPoints: false,
    tTrack: false,
    toollessFeatures: false,
    madeIn: {
      usa: false,
      international: false
    }
  });
  const [filteredFamilies, setFilteredFamilies] = useState([]);
  const [showFilterRemovalDialog, setShowFilterRemovalDialog] = useState(false);
  const [pendingAssemblyChange, setPendingAssemblyChange] = useState(null);
  const [productNamesWithOffers, setProductNamesWithOffers] = useState(null);

  useEffect(() => {
    fetchProductNamesWithOffers().then(setProductNamesWithOffers);
  }, []);

  // Only include shuttles that have at least one active offer (hide HDI DIY kit etc.)
  const visibleShuttles = React.useMemo(() => {
    if (!(productNamesWithOffers instanceof Set) || productNamesWithOffers.size === 0) return shuttlesData;
    return shuttlesData.filter(shuttle => {
      if (!shuttle.csvLookupId) return true;
      return productNamesWithOffers.has(resolveProductNameForSupabase(shuttle.csvLookupId));
    });
  }, [productNamesWithOffers]);

  // Group shuttles by family (like batteries group by base product)
  const shuttleFamilies = React.useMemo(() => {
    const families = {};
    
    visibleShuttles.forEach(shuttle => {
      // Group HDI shuttles together, others stay separate
      let familyKey = shuttle.id;
      let familyName = shuttle.name;
      let hasAssemblyOptions = false;
      
      if (shuttle.id.includes('hdi-shuttle')) {
        familyKey = 'hdi-shuttle-family';
        familyName = 'HDI Shuttle';
        hasAssemblyOptions = true;
      }
      
      if (!families[familyKey]) {
        families[familyKey] = {
          id: familyKey,
          name: familyName,
          hasAssemblyOptions,
          // Use the base product info (assembled version preferred)
          baseProduct: shuttle.assemblyType === 'assembled' ? shuttle : families[familyKey]?.baseProduct || shuttle,
          variants: {}
        };
      }
      
      // Add variants
      if (shuttle.assemblyType === 'assembled') {
        families[familyKey].variants.assembled = shuttle;
        families[familyKey].baseProduct = shuttle; // Use assembled as base
      } else if (shuttle.assemblyType === 'diy') {
        families[familyKey].variants.diy = shuttle;
      } else {
        // Single product without variants
        families[familyKey].variants.single = shuttle;
      }
    });
    
    const result = Object.values(families);
    // Only show assembly toggle when both options exist (e.g. hide DIY toggle if DIY has no offer)
    result.forEach(family => {
      if (family.id === 'hdi-shuttle-family') {
        family.hasAssemblyOptions = !!(family.variants.assembled && family.variants.diy);
      }
    });
    return result;
  }, [visibleShuttles]);

  // Filter function
  const filterShuttles = () => {
    let filtered = shuttleFamilies.filter(family => {
      // Assembly type filter - check if family can provide the desired assembly type
      const assemblyFiltersActive = filters.assemblyType.assembled || filters.assemblyType.diy;
      if (assemblyFiltersActive) {
        let matchesAssemblyFilter = false;
        
        if (filters.assemblyType.assembled) {
          // Show families that have an assembled option
          const hasAssembledOption = family.variants.assembled || family.variants.single?.assemblyType === 'assembled';
          if (hasAssembledOption) matchesAssemblyFilter = true;
        }
        
        if (filters.assemblyType.diy) {
          // Show families that have DIY options
          if (family.hasAssemblyOptions) matchesAssemblyFilter = true;
        }
        
        if (!matchesAssemblyFilter) return false;
      }
      
      // For other filters, check the current variant based on user's assembly preference
      const currentVariant = getCurrentVariant(family);
      
      // Pole holders filter
      if (filters.poleHolders && !currentVariant.poleHolders?.includes('Included')) return false;
      
      // UV Light filter
      if (filters.uvLight && !currentVariant.lighting?.includes('LED')) return false;
      
      // Mounting points filter
      if (filters.mountingPoints && currentVariant.mountingPoints?.includes('None')) return false;
      
      // T-Track filter
      if (filters.tTrack && !currentVariant.tTrack?.includes('T-Track')) return false;
      
      // Tool-less features filter
      if (filters.toollessFeatures && !currentVariant.toollessFeatures?.includes('Tool-less')) return false;
      
      // Made In filter
      const madeInFiltersActive = filters.madeIn.usa || filters.madeIn.international;
      if (madeInFiltersActive) {
        let matchesMadeInFilter = false;
        
        if (filters.madeIn.usa && currentVariant.madeIn?.includes('USA')) {
          matchesMadeInFilter = true;
        }
        
        if (filters.madeIn.international && !currentVariant.madeIn?.includes('USA')) {
          matchesMadeInFilter = true;
        }
        
        if (!matchesMadeInFilter) return false;
      }
      
      return true;
    });
    setFilteredFamilies(filtered);
  };

  // Apply filters when they change
  useEffect(() => {
    filterShuttles();
    
    // If pre-assembled filter is active, force all toggles to assembled
    if (filters.assemblyType.assembled) {
      const updatedPreferences = {};
      shuttleFamilies.forEach(family => {
        if (family.hasAssemblyOptions) {
          updatedPreferences[family.id] = 'assembled';
        }
      });
      
      if (Object.keys(updatedPreferences).length > 0) {
        setAssemblyPreferences(prev => {
          const newPrefs = { ...prev, ...updatedPreferences };
          try {
            localStorage.setItem('shuttleAssemblyPreferences', JSON.stringify(newPrefs));
          } catch (error) {
            console.error('Error saving assembly preferences:', error);
          }
          return newPrefs;
        });
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- filterShuttles closes over filters/shuttleFamilies; listing it would loop
  }, [filters, shuttleFamilies]);

  // Scroll to top when component mounts
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  // Initialize state from existing shuttle selection
  useEffect(() => {
    if (shuttle && shuttle.id) {
      setSelectedShuttleId(shuttle.id);
    }
  }, [shuttle]);

  // Fetch pricing data for shuttles
  useEffect(() => {
    const fetchShuttlePrices = async () => {
      for (const shuttleOption of visibleShuttles) {
        // Check if shuttle has csvLookupId
        if (shuttleOption.csvLookupId) {
          try {
            const result = await fetchProductPrices(
              shuttleOption.id,
              0,
              3,
              shuttleOption.csvLookupId,
              true // exact match only
            );
            
            if (result.success && result.data.length > 0) {
              const sortedData = result.data.sort((a, b) => {
                const priceA = parseFloat(a.Price?.replace(/[$,]/g, '') || '0');
                const priceB = parseFloat(b.Price?.replace(/[$,]/g, '') || '0');
                return priceA - priceB || compareRetailerPreference(a.Source, b.Source);
              });
              
              const lowestPrice = parseFloat(sortedData[0].Price?.replace(/[$,]/g, '') || '0');
              
              // Calculate savings vs MSRP
              const msrpSavings = shuttleOption.msrp && lowestPrice < shuttleOption.msrp 
                ? {
                    amount: (shuttleOption.msrp - lowestPrice).toFixed(2),
                    percent: Math.round(((shuttleOption.msrp - lowestPrice) / shuttleOption.msrp) * 100),
                    reference: 'MSRP'
                  }
                : null;
              
              setShuttlePricing(prev => ({
                ...prev,
                [shuttleOption.id]: {
                  price: lowestPrice.toFixed(2),
                  msrpSavings: msrpSavings,
                  source: sortedData[0].Source,
                  lastUpdated: new Date().toISOString()
                }
              }));
            }
          } catch (error) {
            console.error(`Error fetching prices for shuttle ${shuttleOption.id}:`, error);
          }
        }
      }
    };

    fetchShuttlePrices();
    // eslint-disable-next-line react-hooks/exhaustive-deps -- one-time fetch for visible shuttle list at mount
  }, []);

  // Helper function to format price
  const formatPrice = (price) => {
    if (!price) return '0.00';
    const numPrice = typeof price === 'string' ? parseFloat(price) : price;
    return numPrice.toFixed(2);
  };

  const handleImageError = (e, shuttleName) => {
    if (failedImages[shuttleName]) return;
    
    console.error(`Image failed to load for ${shuttleName}: ${e.target.src}`);
    setFailedImages(prev => ({...prev, [shuttleName]: true}));
    
    // Try to find the shuttle in the families
    const foundFamily = shuttleFamilies.find(family => family.name === shuttleName);
    const shuttleData = foundFamily?.baseProduct;
    
    if (shuttleData && shuttleData.fallbackImage) {
      e.target.src = shuttleData.fallbackImage;
      return;
    }
    
    e.target.src = `https://dummyimage.com/150x100/cccccc/000000&text=${shuttleName.replace(/\s+/g, '+')}`;
    e.target.onerror = null;
  };

  // Assembly toggle handler (like battery charger toggle)
  const handleAssemblyToggle = (familyId, assemblyType) => {
    // If pre-assembled filter is active and user tries to switch to DIY, show confirmation
    if (filters.assemblyType.assembled && assemblyType === 'diy') {
      setPendingAssemblyChange({ familyId, assemblyType });
      setShowFilterRemovalDialog(true);
      return;
    }
    
    const newPreferences = {
      ...assemblyPreferences,
      [familyId]: assemblyType
    };
    setAssemblyPreferences(newPreferences);
    
    // Save to localStorage for persistence
    try {
      localStorage.setItem('shuttleAssemblyPreferences', JSON.stringify(newPreferences));
    } catch (error) {
      console.error('Error saving assembly preferences:', error);
    }
  };

  const handleFilterRemovalConfirm = () => {
    // Remove the pre-assembled filter and apply the pending assembly change
    setFilters(prev => ({ 
      ...prev, 
      assemblyType: { assembled: false, diy: false } 
    }));
    
    if (pendingAssemblyChange) {
      const newPreferences = {
        ...assemblyPreferences,
        [pendingAssemblyChange.familyId]: pendingAssemblyChange.assemblyType
      };
      setAssemblyPreferences(newPreferences);
      
      try {
        localStorage.setItem('shuttleAssemblyPreferences', JSON.stringify(newPreferences));
      } catch (error) {
        console.error('Error saving assembly preferences:', error);
      }
    }
    
    setShowFilterRemovalDialog(false);
    setPendingAssemblyChange(null);
  };

  const handleFilterRemovalCancel = () => {
    setShowFilterRemovalDialog(false);
    setPendingAssemblyChange(null);
  };

  const resetAllFilters = () => {
    setFilters({
      assemblyType: {
        assembled: false,
        diy: false
      },
      poleHolders: false,
      uvLight: false,
      mountingPoints: false,
      tTrack: false,
      toollessFeatures: false,
      madeIn: {
        usa: false,
        international: false
      }
    });
  };

  // Check if any filters are active
  const hasActiveFilters = () => {
    return filters.assemblyType.assembled || 
           filters.assemblyType.diy ||
           filters.poleHolders ||
           filters.uvLight ||
           filters.mountingPoints ||
           filters.tTrack ||
           filters.toollessFeatures ||
           filters.madeIn.usa ||
           filters.madeIn.international;
  };

  // Helper functions that work with family structure
  const getCurrentVariant = (family) => {
    if (!family.hasAssemblyOptions) {
      return family.variants.single || family.baseProduct;
    }
    
    const preference = assemblyPreferences[family.id] || 'diy'; // Default to DIY instead of assembled
    return family.variants[preference] || family.variants.diy || family.variants.assembled || family.baseProduct;
  };

  const getCurrentPrice = (family) => {
    const variant = getCurrentVariant(family);
    return variant.msrp;
  };

  const getDisplayName = (family) => {
    const variant = getCurrentVariant(family);
    return variant.name;
  };

  const getCurrentImage = (family) => {
    const variant = getCurrentVariant(family);
    return variant.image;
  };

  const handleShuttleSelect = (selectedFamily) => {
    const currentVariant = getCurrentVariant(selectedFamily);
    setSelectedShuttleId(currentVariant.id);
    
    // Create shuttle object with current variant information
    const enhancedShuttle = {
      ...currentVariant,
      name: getDisplayName(selectedFamily),
      image: getCurrentImage(selectedFamily),
      currentPrice: getCurrentPrice(selectedFamily)
    };
    
    setShuttle(enhancedShuttle);
  };

  // Function to toggle card expansion
  const toggleCardExpansion = (familyId, event) => {
    event.stopPropagation();
    
    setExpandedCards(prev => {
      const newSet = new Set(prev);
      if (newSet.has(familyId)) {
        newSet.delete(familyId);
      } else {
        newSet.add(familyId);
      }
      return newSet;
    });
  };

  return (
    <div style={{ 
      width: '100%', 
      maxWidth: '100%',
      margin: 0,
      padding: 0,
      backgroundColor: '#000000',
      minHeight: '100vh'
    }}>
      {/* Back to Builder button - outside and above everything else */}
      <div style={{
        backgroundColor: '#000000',
        padding: '10px 0 0 0',
        paddingLeft: '24px'
      }}>
        <button
          onClick={prevStep}
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: '6px',
            background: 'transparent',
            border: 'none',
            color: '#fff',
            fontSize: '14px',
            cursor: 'pointer',
            padding: '0 0 10px 0',
            width: 'fit-content'
          }}
        >
          ← Back to Builder
        </button>
      </div>

      {/* Main Content */}
      <div style={{ 
        display: 'flex',
        margin: 0,
        padding: 0,
        width: '100%',
        backgroundColor: '#000000'
      }}>
        {/* Filters Sidebar */}
        <div style={{
          width: '25%',
          backgroundColor: '#1a1a1a',
          borderRight: '1px solid rgba(255, 255, 255, 0.1)',
          boxShadow: '2px 0 8px rgba(0, 0, 0, 0.2)',
          position: 'sticky',
          top: '75px',
          height: 'calc(100vh - 75px)',
          display: 'flex',
          flexDirection: 'column',
          padding: '10px 15px',
          margin: 0,
          zIndex: 10,
          alignSelf: 'flex-start'
        }}>
          <div style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            marginBottom: '10px'
          }}>
            <h3 style={{ 
              fontSize: '14px',
              margin: '0',
              color: '#ffffff',
              fontWeight: '600'
            }}>Filter Options</h3>
            {hasActiveFilters() && (
              <button
                onClick={resetAllFilters}
                style={{
                  fontSize: '11px',
                  padding: '4px 8px',
                  backgroundColor: 'transparent',
                  border: '1px solid rgba(255, 255, 255, 0.3)',
                  color: '#ffffff',
                  borderRadius: '3px',
                  cursor: 'pointer',
                  opacity: 0.8,
                  transition: 'opacity 0.2s ease'
                }}
                onMouseEnter={(e) => e.target.style.opacity = '1'}
                onMouseLeave={(e) => e.target.style.opacity = '0.8'}
              >
                Reset
              </button>
            )}
          </div>
          
          <div style={{
            overflowY: 'auto',
            padding: '0 0 10px 0',
            flex: 1
          }}>
            {/* Assembly Type Filter – only when at least one shuttle has Assembled vs DIY option */}
            {shuttleFamilies.some(f => f.hasAssemblyOptions) && (
              <div className="filter-section" style={{ 
                marginBottom: '8px',
                paddingBottom: '8px',
                borderBottom: '1px solid rgba(255, 255, 255, 0.1)'
              }}>
                <h4 style={{ 
                  fontSize: '12px',
                  marginBottom: '4px',
                  color: '#b8c7d9',
                  fontWeight: '500'
                }}>Assembly Type</h4>
                <label className="filter-checkbox" style={{
                  fontSize: '12px',
                  marginBottom: '4px',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '4px',
                  color: '#e0e0e0',
                  cursor: 'pointer'
                }}>
                  <input
                    type="checkbox"
                    checked={filters.assemblyType.assembled}
                    onChange={(e) => setFilters(prev => ({ 
                      ...prev, 
                      assemblyType: { ...prev.assemblyType, assembled: e.target.checked }
                    }))}
                    style={{
                      width: '12px',
                      height: '12px',
                      cursor: 'pointer'
                    }}
                  />
                  Pre-assembled
                </label>
                <label className="filter-checkbox" style={{
                  fontSize: '12px',
                  marginBottom: '4px',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '4px',
                  color: '#e0e0e0',
                  cursor: 'pointer'
                }}>
                  <input
                    type="checkbox"
                    checked={filters.assemblyType.diy}
                    onChange={(e) => setFilters(prev => ({ 
                      ...prev, 
                      assemblyType: { ...prev.assemblyType, diy: e.target.checked }
                    }))}
                    style={{
                      width: '12px',
                      height: '12px',
                      cursor: 'pointer'
                    }}
                  />
                  DIY Kit Available
                </label>
              </div>
            )}

            {/* Features Filters */}
            <div className="filter-section" style={{ 
              marginBottom: '8px',
              paddingBottom: '8px',
              borderBottom: '1px solid rgba(255, 255, 255, 0.1)'
            }}>
              <h4 style={{ 
                fontSize: '12px',
                marginBottom: '4px',
                color: '#b8c7d9',
                fontWeight: '500'
              }}>Features</h4>
              <label className="filter-checkbox" style={{
                fontSize: '12px',
                marginBottom: '4px',
                display: 'flex',
                alignItems: 'center',
                gap: '4px',
                color: '#e0e0e0',
                cursor: 'pointer'
              }}>
                <input
                  type="checkbox"
                  checked={filters.poleHolders}
                  onChange={(e) => setFilters(prev => ({ ...prev, poleHolders: e.target.checked }))}
                  style={{
                    width: '12px',
                    height: '12px',
                    cursor: 'pointer'
                  }}
                />
                Pole Holders Included
              </label>
              <label className="filter-checkbox" style={{
                fontSize: '12px',
                marginBottom: '4px',
                display: 'flex',
                alignItems: 'center',
                gap: '4px',
                color: '#e0e0e0',
                cursor: 'pointer'
              }}>
                <input
                  type="checkbox"
                  checked={filters.uvLight}
                  onChange={(e) => setFilters(prev => ({ ...prev, uvLight: e.target.checked }))}
                  style={{
                    width: '12px',
                    height: '12px',
                    cursor: 'pointer'
                  }}
                />
                UV Light Included
              </label>
              <label className="filter-checkbox" style={{
                fontSize: '12px',
                marginBottom: '4px',
                display: 'flex',
                alignItems: 'center',
                gap: '4px',
                color: '#e0e0e0',
                cursor: 'pointer'
              }}>
                <input
                  type="checkbox"
                  checked={filters.mountingPoints}
                  onChange={(e) => setFilters(prev => ({ ...prev, mountingPoints: e.target.checked }))}
                  style={{
                    width: '12px',
                    height: '12px',
                    cursor: 'pointer'
                  }}
                />
                Mounting Points Included
              </label>
              <label className="filter-checkbox" style={{
                fontSize: '12px',
                marginBottom: '4px',
                display: 'flex',
                alignItems: 'center',
                gap: '4px',
                color: '#e0e0e0',
                cursor: 'pointer'
              }}>
                <input
                  type="checkbox"
                  checked={filters.tTrack}
                  onChange={(e) => setFilters(prev => ({ ...prev, tTrack: e.target.checked }))}
                  style={{
                    width: '12px',
                    height: '12px',
                    cursor: 'pointer'
                  }}
                />
                T-Track System
              </label>
              <label className="filter-checkbox" style={{
                fontSize: '12px',
                marginBottom: '4px',
                display: 'flex',
                alignItems: 'center',
                gap: '4px',
                color: '#e0e0e0',
                cursor: 'pointer'
              }}>
                <input
                  type="checkbox"
                  checked={filters.toollessFeatures}
                  onChange={(e) => setFilters(prev => ({ ...prev, toollessFeatures: e.target.checked }))}
                  style={{
                    width: '12px',
                    height: '12px',
                    cursor: 'pointer'
                  }}
                />
                Tool-less Access
              </label>
            </div>

            {/* Made In Filter */}
            <div className="filter-section" style={{ 
              marginBottom: '8px',
              paddingBottom: '8px'
            }}>
              <h4 style={{ 
                fontSize: '12px',
                marginBottom: '4px',
                color: '#b8c7d9',
                fontWeight: '500'
              }}>Made In</h4>
              <label className="filter-checkbox" style={{
                fontSize: '12px',
                marginBottom: '4px',
                display: 'flex',
                alignItems: 'center',
                gap: '4px',
                color: '#e0e0e0',
                cursor: 'pointer'
              }}>
                <input
                  type="checkbox"
                  checked={filters.madeIn.usa}
                  onChange={(e) => setFilters(prev => ({ 
                    ...prev, 
                    madeIn: { ...prev.madeIn, usa: e.target.checked }
                  }))}
                  style={{
                    width: '12px',
                    height: '12px',
                    cursor: 'pointer'
                  }}
                />
                Made in USA
              </label>
              <label className="filter-checkbox" style={{
                fontSize: '12px',
                marginBottom: '4px',
                display: 'flex',
                alignItems: 'center',
                gap: '4px',
                color: '#e0e0e0',
                cursor: 'pointer'
              }}>
                <input
                  type="checkbox"
                  checked={filters.madeIn.international}
                  onChange={(e) => setFilters(prev => ({ 
                    ...prev, 
                    madeIn: { ...prev.madeIn, international: e.target.checked }
                  }))}
                  style={{
                    width: '12px',
                    height: '12px',
                    cursor: 'pointer'
                  }}
                />
                International
              </label>
            </div>
          </div>
        </div>

        {/* Main Content Area */}
        <div style={{
          width: '75%',
          padding: '0 20px 20px 20px',
          backgroundColor: '#000000'
        }}>
          <h2 style={{
            color: '#ffffff',
            fontSize: '22px',
            marginBottom: '10px',
            marginTop: '0px'
          }}>Shuttle Selection</h2>
          
          {filteredFamilies.length === 0 ? (
            <div style={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              padding: '40px 20px',
              color: '#b8c7d9',
              textAlign: 'center',
              backgroundColor: 'rgba(255, 255, 255, 0.05)',
              borderRadius: '8px',
              margin: '20px 0'
            }}>
              <span style={{ fontSize: '24px', marginBottom: '12px' }}>🔍</span>
              <h3 style={{ 
                margin: '0 0 8px 0',
                color: '#ffffff',
                fontSize: '18px'
              }}>No Matching Shuttles Found</h3>
              <p style={{ 
                margin: '0',
                fontSize: '14px',
                maxWidth: '400px',
                lineHeight: '1.5'
              }}>
                Try adjusting your filters to see more options. Consider changing the feature requirements or assembly type.
              </p>
            </div>
          ) : (
            <GridContainer>
        {filteredFamilies.map(shuttleFamily => {
          const currentVariant = getCurrentVariant(shuttleFamily);
          const isDIY = (assemblyPreferences[shuttleFamily.id] || 'diy') === 'diy'; // Default to DIY
          
          return (
            <div key={shuttleFamily.id} style={{
              backgroundColor: '#1a1a1a',
              borderRadius: '8px',
              overflow: 'hidden',
              width: '100%'
            }}>
              <ImageContainer onClick={() => setModalImage({ isOpen: true, url: getCurrentImage(shuttleFamily), alt: getDisplayName(shuttleFamily) })}>
                <ProductImage 
                  src={getCurrentImage(shuttleFamily)} 
                  alt={getDisplayName(shuttleFamily)}
                  onError={(e) => handleImageError(e, shuttleFamily.name)}
                />
                <div style={{
                  position: 'absolute',
                  top: '8px',
                  right: '8px',
                  backgroundColor: 'rgba(0, 0, 0, 0.5)',
                  borderRadius: '50%',
                  width: '24px',
                  height: '24px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}>
                  <svg 
                    width="14" 
                    height="14" 
                    viewBox="0 0 24 24" 
                    fill="none" 
                    stroke="white" 
                    strokeWidth="2" 
                    strokeLinecap="round" 
                    strokeLinejoin="round"
                  >
                    <circle cx="11" cy="11" r="8"></circle>
                    <line x1="21" y1="21" x2="16.65" y2="16.65"></line>
                  </svg>
                </div>
              </ImageContainer>

              <div style={{ padding: '12px' }}>
                <h3 style={{ fontSize: '0.85em', margin: '0 0 4px 0', color: '#fff' }}>
                  {getDisplayName(shuttleFamily)}
                </h3>
                {(() => {
                  const shuttlePricingData = shuttlePricing[currentVariant.id];
                  const hasLowestPrice = shuttlePricingData && shuttlePricingData.price;

                  if (hasLowestPrice) {
                    return (
                      <div style={{ fontSize: '0.85em', margin: '0 0 8px 0' }}>
                        <p style={{ color: '#4CAF50', margin: '0 0 4px 0', display: 'flex', alignItems: 'center', gap: 4 }}>
                          Best Price: ${formatPrice(shuttlePricingData.price)}
                        </p>
                        {shuttlePricingData.msrpSavings && (
                          <div style={{
                            marginTop: '4px',
                            fontSize: '12px',
                            color: '#e67e22',
                            fontWeight: '500'
                          }}>
                            ↓ Save ${formatPrice(shuttlePricingData.msrpSavings.amount)} off {shuttlePricingData.msrpSavings.reference || 'MSRP'}
                          </div>
                        )}
                      </div>
                    );
                  } else if (currentVariant.csvLookupId) {
                    // Show loading for products with CSV lookup ID
                    return (
                      <div style={{ fontSize: '0.85em', margin: '0 0 8px 0' }}>
                        <div style={{ fontSize: '0.8em', color: '#4CAF50', marginTop: '2px', display: 'flex', alignItems: 'center', gap: 4 }}>
                          📡 Loading best price...
                        </div>
                        <p style={{ color: '#b8c7d9', margin: '4px 0 0 0', fontSize: '0.8em' }}>
                          MSRP: ${formatPrice(currentVariant.msrp)}
                        </p>
                      </div>
                    );
                  } else {
                    // Show MSRP for products without CSV lookup ID
                    return (
                      <div style={{ fontSize: '0.85em', margin: '0 0 8px 0' }}>
                        <p style={{ color: '#4CAF50', margin: '0 0 4px 0' }}>
                          MSRP: ${formatPrice(currentVariant.msrp)}
                        </p>
                      </div>
                    );
                  }
                })()}

                {/* Assembly Toggle Bar - like battery charger toggle */}
                {shuttleFamily.hasAssemblyOptions && (
                  <div style={{
                    display: 'flex',
                    gap: '1px',
                    marginBottom: '10px',
                    backgroundColor: '#233446',
                    padding: '1px',
                    borderRadius: '4px',
                    width: '100%'
                  }}>
                    <button
                      onClick={() => handleAssemblyToggle(shuttleFamily.id, 'diy')}
                      style={{
                        flex: 1,
                        padding: '8px 12px',
                        fontSize: '12px',
                        borderRadius: '3px',
                        border: 'none',
                        cursor: 'pointer',
                        fontWeight: '600',
                        backgroundColor: isDIY ? '#4CAF50' : 'transparent',
                        color: '#ffffff',
                        transition: 'all 0.2s ease'
                      }}
                    >
                      DIY Kit
                    </button>
                    <button
                      onClick={() => handleAssemblyToggle(shuttleFamily.id, 'assembled')}
                      style={{
                        flex: 1,
                        padding: '8px 12px',
                        fontSize: '12px',
                        borderRadius: '3px',
                        border: 'none',
                        cursor: 'pointer',
                        fontWeight: '600',
                        backgroundColor: !isDIY ? '#4CAF50' : 'transparent',
                        color: '#ffffff',
                        transition: 'all 0.2s ease'
                      }}
                    >
                      Assembled
                    </button>
                  </div>
                )}
                {/* No assembly callout when all shuttles are assembled-only – avoids redundant "Assembled" on every card */}

                {/* Detailed specs section */}
                <div className="shuttle-specs" style={{ 
                  display: 'flex', 
                  flexDirection: 'column', 
                  gap: '0',
                  fontSize: '0.9em',
                  marginBottom: '10px'
                }}>
                  {/* Descriptive section with best for badge and benefits */}
                  <div style={{ marginBottom: 10, fontSize: '0.82em' }}>
                    <div style={{ fontWeight: 700, fontSize: '1em', marginBottom: 2 }}>
                      {currentVariant.bestFor ? `Best for ${currentVariant.bestFor}` : 'Reliable Performance'}
                    </div>
                    <div style={{ marginBottom: 8 }}>
                      {currentVariant.description}
                    </div>
                    {currentVariant.benefits && (
                      <ul style={{ listStyle: 'none', padding: 0, margin: 0 }}>
                        {currentVariant.benefits.map((benefit, index) => (
                          <li key={index} style={{ display: 'flex', alignItems: 'center', marginBottom: 2 }}>
                            <span style={{ color: '#22c55e', fontSize: '1.2em', marginRight: 6 }}>✅</span>
                            {benefit}
                          </li>
                        ))}
                      </ul>
                    )}
                  </div>

                  {/* Key specs - show first 3, then expandable */}
                  {!shuttleFamily.hasAssemblyOptions && (
                    <div className="spec-row" style={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      padding: '4px 2px',
                      borderBottom: '1px solid rgba(255, 255, 255, 0.1)'
                    }}>
                      <span className="spec-label" style={{ color: '#b8c7d9', fontSize: '0.9em', flex: '0 0 45%' }}>Assembly</span>
                      <span className="spec-value" style={{ color: '#ffffff', fontSize: '0.9em', flex: '0 0 55%', textAlign: 'right', paddingLeft: '4px', paddingRight: '2px' }}>Fully assembled</span>
                    </div>
                  )}
                  <div className="spec-row" style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    padding: '4px 2px',
                    borderBottom: '1px solid rgba(255, 255, 255, 0.1)'
                  }}>
                    <span className="spec-label" style={{ color: '#b8c7d9', fontSize: '0.9em', flex: '0 0 45%' }}>Weight</span>
                    <span className="spec-value" style={{ color: '#ffffff', fontSize: '0.9em', flex: '0 0 55%', textAlign: 'right', paddingLeft: '4px', paddingRight: '2px' }}>{currentVariant.weight || 'Not specified'}</span>
                  </div>
                  <div className="spec-row" style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    padding: '4px 2px',
                    borderBottom: '1px solid rgba(255, 255, 255, 0.1)'
                  }}>
                    <span className="spec-label" style={{ color: '#b8c7d9', fontSize: '0.9em', flex: '0 0 45%' }}>UV Light</span>
                    <span className="spec-value" style={{ color: '#ffffff', fontSize: '0.9em', flex: '0 0 55%', textAlign: 'right', paddingLeft: '4px', paddingRight: '2px' }}>{currentVariant.lighting || 'Not specified'}</span>
                  </div>
                  <div className="spec-row" style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    padding: '4px 2px',
                    borderBottom: '1px solid rgba(255, 255, 255, 0.1)'
                  }}>
                    <span className="spec-label" style={{ color: '#b8c7d9', fontSize: '0.9em', flex: '0 0 45%' }}>USB Ports</span>
                    <span className="spec-value" style={{ color: '#ffffff', fontSize: '0.9em', flex: '0 0 55%', textAlign: 'right', paddingLeft: '4px', paddingRight: '2px' }}>{currentVariant.usbPorts || 'Not specified'}</span>
                  </div>

                  {/* Expandable specs */}
                  {expandedCards.has(shuttleFamily.id) ? (
                    <>
                      {/* Additional specs when expanded */}
                      <div className="spec-row" style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        padding: '4px 2px',
                        borderBottom: '1px solid rgba(255, 255, 255, 0.1)'
                      }}>
                        <span className="spec-label" style={{ color: '#b8c7d9', fontSize: '0.9em', flex: '0 0 45%' }}>Pole Holders</span>
                        <span className="spec-value" style={{ color: '#ffffff', fontSize: '0.9em', flex: '0 0 55%', textAlign: 'right', paddingLeft: '4px', paddingRight: '2px' }}>{currentVariant.poleHolders || 'Not specified'}</span>
                      </div>
                      <div className="spec-row" style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        padding: '4px 2px',
                        borderBottom: '1px solid rgba(255, 255, 255, 0.1)'
                      }}>
                        <span className="spec-label" style={{ color: '#b8c7d9', fontSize: '0.9em', flex: '0 0 45%' }}>Load Capacity</span>
                        <span className="spec-value" style={{ color: '#ffffff', fontSize: '0.9em', flex: '0 0 55%', textAlign: 'right', paddingLeft: '4px', paddingRight: '2px' }}>{currentVariant.loadCapacity || 'Not specified'}</span>
                      </div>
                      <div className="spec-row" style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        padding: '4px 2px',
                        borderBottom: '1px solid rgba(255, 255, 255, 0.1)'
                      }}>
                        <span className="spec-label" style={{ color: '#b8c7d9', fontSize: '0.9em', flex: '0 0 45%' }}>Mounting Points</span>
                        <span className="spec-value" style={{ color: '#ffffff', fontSize: '0.9em', flex: '0 0 55%', textAlign: 'right', paddingLeft: '4px', paddingRight: '2px' }}>{currentVariant.mountingPoints || 'Not specified'}</span>
                      </div>
                      <div className="spec-row" style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        padding: '4px 2px',
                        borderBottom: '1px solid rgba(255, 255, 255, 0.1)'
                      }}>
                        <span className="spec-label" style={{ color: '#b8c7d9', fontSize: '0.9em', flex: '0 0 45%' }}>T-Track</span>
                        <span className="spec-value" style={{ color: '#ffffff', fontSize: '0.9em', flex: '0 0 55%', textAlign: 'right', paddingLeft: '4px', paddingRight: '2px' }}>{currentVariant.tTrack || 'Not specified'}</span>
                      </div>
                      <div className="spec-row" style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        padding: '4px 2px',
                        borderBottom: '1px solid rgba(255, 255, 255, 0.1)'
                      }}>
                        <span className="spec-label" style={{ color: '#b8c7d9', fontSize: '0.9em', flex: '0 0 45%' }}>Tool-less Features</span>
                        <span className="spec-value" style={{ color: '#ffffff', fontSize: '0.9em', flex: '0 0 55%', textAlign: 'right', paddingLeft: '4px', paddingRight: '2px' }}>{currentVariant.toollessFeatures || 'Not specified'}</span>
                      </div>
                      <div className="spec-row" style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        padding: '4px 2px',
                        borderBottom: '1px solid rgba(255, 255, 255, 0.1)'
                      }}>
                        <span className="spec-label" style={{ color: '#b8c7d9', fontSize: '0.9em', flex: '0 0 45%' }}>Wiring Harness</span>
                        <span className="spec-value" style={{ color: '#ffffff', fontSize: '0.9em', flex: '0 0 55%', textAlign: 'right', paddingLeft: '4px', paddingRight: '2px' }}>{currentVariant.wiringHarness || 'Not specified'}</span>
                      </div>
                      <div className="spec-row" style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        padding: '4px 2px',
                        borderBottom: '1px solid rgba(255, 255, 255, 0.1)'
                      }}>
                        <span className="spec-label" style={{ color: '#b8c7d9', fontSize: '0.9em', flex: '0 0 45%' }}>Made In</span>
                        <span className="spec-value" style={{ color: '#ffffff', fontSize: '0.9em', flex: '0 0 55%', textAlign: 'right', paddingLeft: '4px', paddingRight: '2px' }}>{currentVariant.madeIn || 'Not specified'}</span>
                      </div>
                      <div className="spec-row" style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        padding: '4px 2px',
                        borderBottom: '1px solid rgba(255, 255, 255, 0.1)'
                      }}>
                        <span className="spec-label" style={{ color: '#b8c7d9', fontSize: '0.9em', flex: '0 0 45%' }}>Construction</span>
                        <span className="spec-value" style={{ color: '#ffffff', fontSize: '0.9em', flex: '0 0 55%', textAlign: 'right', paddingLeft: '4px', paddingRight: '2px' }}>{currentVariant.construction || 'Not specified'}</span>
                      </div>
                      <div className="spec-row" style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        padding: '4px 2px',
                        borderBottom: '1px solid rgba(255, 255, 255, 0.1)'
                      }}>
                        <span className="spec-label" style={{ color: '#b8c7d9', fontSize: '0.9em', flex: '0 0 45%' }}>Screen Size</span>
                        <span className="spec-value" style={{ color: '#ffffff', fontSize: '0.9em', flex: '0 0 55%', textAlign: 'right', paddingLeft: '4px', paddingRight: '2px' }}>{currentVariant.screenCompatibility || 'Not specified'}</span>
                      </div>
                      <div className="spec-row" style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        padding: '4px 2px',
                        borderBottom: '1px solid rgba(255, 255, 255, 0.1)'
                      }}>
                        <span className="spec-label" style={{ color: '#b8c7d9', fontSize: '0.9em', flex: '0 0 45%' }}>Battery Type</span>
                        <span className="spec-value" style={{ color: '#ffffff', fontSize: '0.9em', flex: '0 0 55%', textAlign: 'right', paddingLeft: '4px', paddingRight: '2px' }}>{currentVariant.batteryCompatibility || 'Not specified'}</span>
                      </div>
                      <div className="spec-row" style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        padding: '4px 2px',
                        borderBottom: '1px solid rgba(255, 255, 255, 0.1)'
                      }}>
                        <span className="spec-label" style={{ color: '#b8c7d9', fontSize: '0.9em', flex: '0 0 45%' }}>Battery Tray</span>
                        <span className="spec-value" style={{ color: '#ffffff', fontSize: '0.9em', flex: '0 0 55%', textAlign: 'right', paddingLeft: '4px', paddingRight: '2px' }}>{currentVariant.batteryTrayDimensions || 'Not specified'}</span>
                      </div>
                      {currentVariant.assemblyTime && (
                        <div className="spec-row" style={{
                          display: 'flex',
                          justifyContent: 'space-between',
                          alignItems: 'center',
                          padding: '4px 2px',
                          borderBottom: '1px solid rgba(255, 255, 255, 0.1)'
                        }}>
                          <span className="spec-label" style={{ color: '#b8c7d9', fontSize: '0.9em', flex: '0 0 45%' }}>Assembly Time</span>
                          <span className="spec-value" style={{ color: '#ffffff', fontSize: '0.9em', flex: '0 0 55%', textAlign: 'right', paddingLeft: '4px', paddingRight: '2px' }}>
                            {currentVariant.assemblyTime}
                          </span>
                        </div>
                      )}
                    </>
                  ) : (
                    // Show a faded version of the 4th feature when not expanded
                    <div className="spec-row" style={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      padding: '4px 2px',
                      borderBottom: '1px solid rgba(255, 255, 255, 0.1)',
                      opacity: 0.6,
                      position: 'relative'
                    }}>
                      <span className="spec-label" style={{ color: '#b8c7d9', fontSize: '0.9em', flex: '0 0 45%' }}>Load Capacity</span>
                      <span className="spec-value" style={{ color: '#ffffff', fontSize: '0.9em', flex: '0 0 55%', textAlign: 'right', paddingLeft: '4px', paddingRight: '2px' }}>{currentVariant.loadCapacity || 'Not specified'}</span>
                      {/* Gradient overlay to fade the content */}
                      <div style={{
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        right: 0,
                        bottom: 0,
                        background: 'linear-gradient(to bottom, rgba(26, 26, 26, 0) 0%, rgba(26, 26, 26, 0.8) 100%)',
                        pointerEvents: 'none'
                      }} />
                    </div>
                  )}
                  
                  {/* Show/Hide button */}
                  <button
                    onClick={(e) => toggleCardExpansion(shuttleFamily.id, e)}
                    style={{
                      background: 'transparent',
                      border: 'none',
                      color: '#b8c7d9',
                      width: '100%',
                      padding: '4px 0',
                      marginTop: '4px',
                      fontSize: '0.9em',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      gap: '4px',
                      cursor: 'pointer'
                    }}
                  >
                    {expandedCards.has(shuttleFamily.id) ? (
                      <>Show Less <span style={{ fontSize: '0.8em', color: '#b8c7d9' }}>▲</span></>
                    ) : (
                      <>Show More Specs <span style={{ fontSize: '0.8em', color: '#b8c7d9' }}>▼</span></>
                    )}
                  </button>
                </div>

                {/* Compact Styled Action Buttons */}
                <div style={{
                  marginTop: '8px',
                  padding: '6px',
                  background: 'rgba(0, 0, 0, 0.3)',
                  borderRadius: '6px',
                  border: '1px solid rgba(255, 255, 255, 0.1)'
                }}>
                  <div style={{
                    textAlign: 'center',
                    marginBottom: '6px',
                    color: '#ffffff',
                    fontSize: '0.6em',
                    fontWeight: '600',
                    opacity: 0.9,
                    width: '100%',
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center'
                  }}>
                    Choose Your Next Step:
                  </div>
                  
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                    {currentVariant.csvLookupId && (
                      <div style={{ textAlign: 'center', width: '100%' }}>
                        <button
                          onClick={() => {
                            // If priceComparisonUrl already includes the full path, use it directly
                            // Otherwise, construct the path with /simple-products/ prefix
                            const productUrl = currentVariant.priceComparisonUrl?.startsWith('/') 
                              ? currentVariant.priceComparisonUrl 
                              : `/simple-products/${currentVariant.priceComparisonUrl || currentVariant.id}`;
                            navigate(productUrl);
                          }}
                          style={{
                            width: '100%',
                            fontSize: '0.7em',
                            padding: '6px 8px',
                            background: 'linear-gradient(135deg, #28a745 0%, #1e7e34 100%)',
                            border: 'none',
                            color: 'white',
                            borderRadius: '4px',
                            fontWeight: '600',
                            textAlign: 'center',
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'center',
                            justifyContent: 'center',
                            boxSizing: 'border-box',
                            margin: '0',
                            cursor: 'pointer',
                            transition: 'all 0.2s ease'
                          }}
                          onMouseEnter={(e) => {
                            e.target.style.background = 'linear-gradient(135deg, #34ce57 0%, #28a745 100%)';
                            e.target.style.transform = 'translateY(-1px)';
                          }}
                          onMouseLeave={(e) => {
                            e.target.style.background = 'linear-gradient(135deg, #28a745 0%, #1e7e34 100%)';
                            e.target.style.transform = 'translateY(0)';
                          }}
                        >
                          <div style={{ lineHeight: '1.0' }}>💰 Find Best Price</div>
                          <div style={{
                            fontSize: '0.8em',
                            fontWeight: '400',
                            opacity: 0.9,
                            marginTop: '1px',
                            lineHeight: '1.0'
                          }}>
                            Compare prices across stores
                          </div>
                        </button>
                      </div>
                    )}
                    
                    {currentVariant.csvLookupId && (
                      <div style={{
                        textAlign: 'center',
                        fontSize: '0.55em',
                        color: '#aaa',
                        margin: '0',
                        width: '100%',
                        display: 'flex',
                        justifyContent: 'center',
                        alignItems: 'center'
                      }}>
                        or
                      </div>
                    )}
                    
                    <button 
                       onClick={() => handleShuttleSelect(shuttleFamily)}
                       style={{
                         width: '100%',
                         padding: '6px 8px',
                         fontSize: '0.75em',
                         background: 'linear-gradient(135deg, #0066cc 0%, #004499 100%)',
                         border: 'none',
                         color: 'white',
                         borderRadius: '4px',
                         cursor: 'pointer',
                         fontWeight: '600',
                         transition: 'all 0.2s ease',
                         textAlign: 'center',
                         display: 'flex',
                         flexDirection: 'column',
                         alignItems: 'center',
                         justifyContent: 'center',
                         gap: '2px'
                       }}
                       onMouseEnter={(e) => {
                         e.target.style.background = 'linear-gradient(135deg, #0077dd 0%, #0055aa 100%)';
                         e.target.style.transform = 'translateY(-1px)';
                       }}
                       onMouseLeave={(e) => {
                         e.target.style.background = 'linear-gradient(135deg, #0066cc 0%, #004499 100%)';
                         e.target.style.transform = 'translateY(0)';
                       }}
                     >
                       <div style={{ lineHeight: '1.1' }}>
                         🔧 Select & Build Setup
                       </div>
                       <div style={{
                         fontSize: '0.85em',
                         fontWeight: '400',
                         opacity: 0.9,
                         lineHeight: '1.1'
                       }}>
                         Add screen, transducer, etc.
                       </div>
                     </button>
                  </div>
                </div>
              </div>
            </div>
          );
        })}
      </GridContainer>
          )}
        </div>
      </div>

      {modalImage.isOpen && (
        <ImageModal
          isOpen={modalImage.isOpen}
          imageUrl={modalImage.url}
          altText={modalImage.alt}
          onClose={() => setModalImage({ isOpen: false, url: '', alt: '' })}
        />
      )}

      {/* Filter Removal Confirmation Dialog */}
      {showFilterRemovalDialog && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.7)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 2000
        }}>
          <div style={{
            backgroundColor: '#1a1a1a',
            borderRadius: '8px',
            padding: '24px',
            maxWidth: '400px',
            margin: '20px',
            border: '1px solid rgba(255, 255, 255, 0.1)'
          }}>
            <h3 style={{
              color: '#ffffff',
              margin: '0 0 16px 0',
              fontSize: '18px'
            }}>
              Remove Pre-assembled Filter?
            </h3>
            <p style={{
              color: '#b8c7d9',
              margin: '0 0 24px 0',
              lineHeight: '1.5',
              fontSize: '14px'
            }}>
              To switch to the DIY Kit option, the "Pre-assembled" filter will need to be removed. This will show all shuttle options again.
            </p>
            <div style={{
              display: 'flex',
              gap: '12px',
              justifyContent: 'flex-end'
            }}>
              <button
                onClick={handleFilterRemovalCancel}
                style={{
                  padding: '8px 16px',
                  fontSize: '14px',
                  backgroundColor: 'transparent',
                  border: '1px solid rgba(255, 255, 255, 0.3)',
                  color: '#ffffff',
                  borderRadius: '4px',
                  cursor: 'pointer'
                }}
              >
                Cancel
              </button>
              <button
                onClick={handleFilterRemovalConfirm}
                style={{
                  padding: '8px 16px',
                  fontSize: '14px',
                  backgroundColor: '#4CAF50',
                  border: 'none',
                  color: '#ffffff',
                  borderRadius: '4px',
                  cursor: 'pointer'
                }}
              >
                Remove Filter & Continue
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ShuttleSelection;
